import debounce from 'lodash/debounce';
import type { ChangeEvent, KeyboardEvent } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchBox } from 'react-instantsearch';
import { useIntl } from 'react-intl';

import { isEyewearEnabled } from '@/utils/eyewear';

import { useClickOutside } from '@/hooks/useClickOutside';
import { useGoogleAnalytics } from '@/hooks/useGoogleAnalytics';
import { useRouter } from '@/hooks/useRouter';
import {
  applyProductConfigurationFilters,
  getWatchFilters,
  performMultiSearch,
  ProductConfigurationFilterType,
} from '@/integrations/algolia/helpers';
import type { SearchHit } from '@/integrations/algolia/types';
import { DEBOUNCE_TIME_IN_MS, HITS_PER_PAGE } from '@/lib/const';
import { EVENT_NAME } from '@/lib/gtm/const';
import type { WithHTMLProp } from '@/lib/types';
import { useRegionProvider } from '@/providers/RegionProvider';
import { useSearchState } from '@/states/searchState';

import { getHitName } from './utils';

import * as S from './AlgoliaSearchInput.styles';
import * as M from './messages';

interface AlgoliaSearchInputProps extends WithHTMLProp<'className'> {
  clearOnUnmount?: boolean;
  draftMode: boolean;
  placeholder?: string | null;
}

export const AlgoliaSearchInput = ({
  className,
  clearOnUnmount = false,
  draftMode,
}: AlgoliaSearchInputProps) => {
  const { formatMessage } = useIntl();
  const { overlayStatus } = useSearchState();

  const { query, refine } = useSearchBox();
  const { locale, paths, push } = useRouter(); // when bowser click back button, query do not change
  const { locale: regionLocale } = useRegionProvider();
  const {
    country: { code },
  } = useRegionProvider();
  const [inputValue, setInputValue] = useState<string>(query);

  const [isFocused, setIsFocused] = useState(false);
  const [hits, setHits] = useState<SearchHit[]>([]);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const loadingRef = useRef<boolean>(false);

  const { sendEvent } = useGoogleAnalytics();

  //sync query, inputValue
  useEffect(() => {
    if (inputValue !== query) {
      setInputValue(query);
    }
  }, [query]);

  useClickOutside(inputRef, () => {
    setIsFocused(false);
  });

  const doSearch = useCallback(
    debounce(async (searchTerm: string) => {
      setIsFocused(false);
      refine(searchTerm);

      const queryParams: Record<string, string> = {};
      if (searchTerm) {
        queryParams['q'] = searchTerm;
      }

      await push(paths.search.all.url({ query: queryParams }));

      loadingRef.current = false;
    }, DEBOUNCE_TIME_IN_MS),
    [],
  );

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      sendEvent({
        event: EVENT_NAME.SEARCH,
        search_term: inputValue,
      });

      inputRef?.current?.blur();
      doSearch(inputValue);
    }
  };

  const handleIconClick = () => {
    if (loadingRef.current) return;
    loadingRef.current = true;
    sendEvent({
      event: EVENT_NAME.SEARCH,
      search_term: inputValue,
    });

    doSearch(inputValue);
  };

  const handleHintClick = (hit: SearchHit) => {
    setIsFocused(false);

    if (!isWatchSku) {
      const hitName = getHitName(hit, regionLocale);
      if (hitName) {
        setInputValue(hitName);
        doSearch(hitName);
      } else {
        doSearch(inputValue);
      }
    } else {
      doSearch(inputValue);
    }
  };

  const [isWatchSku, setIsWatchSku] = useState(false);
  const handleChange = useCallback(
    debounce(async (value: string) => {
      const defaultFilters = applyProductConfigurationFilters(
        ProductConfigurationFilterType.SEARCH,
        undefined,
        draftMode,
      );
      const [watchFilters, isWatchSkuArray] = getWatchFilters(
        value,
        defaultFilters,
      );
      const localIsWatchSku =
        isWatchSkuArray.length > 0 ? isWatchSkuArray[0] : false;
      setIsWatchSku(localIsWatchSku);
      const filters = localIsWatchSku ? watchFilters : defaultFilters;
      const queryType = 'prefixAll';
      const typoTolerance = 'min';
      const { results } = await performMultiSearch(
        value,
        locale,
        {
          hitsPerPage: HITS_PER_PAGE,
          filters: filters,
        },
        draftMode,
        code,
        queryType,
        typoTolerance,
      );
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const hits = results.flatMap(({ hits }: any) => hits);

      const uniqueByNameMap = new Map<string, SearchHit>();

      for (const hit of hits) {
        let name = '';

        if (
          hit.product_type === 'Watch' ||
          hit.product_type === 'Strap' ||
          hit.product_type === 'Eyewear'
        ) {
          const watchName = hit.name[regionLocale.code.toUpperCase()];
          name = typeof watchName === 'string' ? watchName : '';
        } else if (hit.type && typeof hit.type === 'string') {
          name = getGroupLabel(hit.type);
        } else {
          name = 'Others';
        }
        if (name && !uniqueByNameMap.has(name)) {
          uniqueByNameMap.set(name, hit);
        }
      }

      const uniqueHits = Array.from(uniqueByNameMap.values());

      setHits(uniqueHits);
    }, DEBOUNCE_TIME_IN_MS),
    [],
  );

  useEffect(() => {
    // NOSONAR: long existing logic, works as expected
    if (overlayStatus === 'CLOSE' && clearOnUnmount) {
      setInputValue('');
    } else if (overlayStatus === 'OPEN') {
      inputRef.current?.focus();
    }
  }, [overlayStatus]);

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (!isFocused) {
      setIsFocused(true);
    }
    setInputValue(e.target.value);
    handleChange(e.target.value);
  };

  function getGroupKey(hit: SearchHit): string {
    if ('product_type' in hit) {
      return hit.product_type;
    } else if ('type' in hit) {
      return hit.type;
    }
    return 'OTHER';
  }

  const getGroupLabel = (groupKey: string): string => {
    switch (groupKey.toUpperCase()) {
      case 'WATCH':
        return 'Watches';
      case 'STRAP':
        return 'Straps';
      case 'EYEWEAR':
        return 'Sunglasses';
      case 'OFFICIAL_RETAILER':
        return 'Official Retailers';
      case 'BOUTIQUE':
        return 'Boutiques';
      case 'SERVICE_CENTER':
        return 'Service Centers';
      case 'OUTLETS':
        return 'Outlets';
      default:
        return 'Others';
    }
  };

  const groupHits = (allHits: SearchHit[]) => {
    const groupingMap = new Map<string, SearchHit[]>();

    allHits.forEach((hit) => {
      const key = getGroupKey(hit);
      if (!groupingMap.has(key)) {
        groupingMap.set(key, [hit]);
      } else {
        groupingMap.get(key)!.push(hit);
      }
    });

    return Array.from(groupingMap.entries()).map(([groupKey, hits]) => ({
      groupKey,
      hits,
    }));
  };

  let groupedHits = groupHits(hits);
  if (!isEyewearEnabled(draftMode)) {
    groupedHits = groupedHits.filter((group) => group.groupKey !== 'Eyewear');
  }

  return (
    <S.Wrapper className={className} data-test-id='AlgoliaSearchInputWrapper'>
      <S.InputWrapper data-test-id='AlgoliaSearchInputInputWrapper'>
        <S.Input
          ref={inputRef}
          data-test-id='AlgoliaSearchInputInput'
          onChange={handleInputChange}
          onClick={() => setIsFocused(true)}
          onFocus={() => setIsFocused(true)}
          onKeyDown={handleKeyDown}
          placeholder={formatMessage(M.defined.whatAreYouLookingFor)}
          value={inputValue}
        />
        <S.SearchIcon
          data-testid='AlgoliaSearchInputSearchIcon'
          onClick={handleIconClick}
        />
      </S.InputWrapper>

      {isFocused && inputValue && (
        <S.Results data-testid='AlgoliaSearchInputResults'>
          {groupedHits.map(({ groupKey, hits }, groupIndex) => (
            <div key={groupKey}>
              <S.GroupLabel as='div'>{getGroupLabel(groupKey)}</S.GroupLabel>

              {hits.map((hit, index) => {
                const nameString = getHitName(hit, regionLocale) ?? '';

                return (
                  <S.Result
                    key={`${hit.objectID}-${index}`}
                    dangerouslySetInnerHTML={{
                      __html: nameString,
                    }}
                    data-test-id={`AlgoliaSearchInputResult-${groupIndex}-${index}`}
                    onClick={() => handleHintClick(hit)}
                  />
                );
              })}
              {groupIndex < groupedHits.length - 1 && <S.Separator />}
            </div>
          ))}
        </S.Results>
      )}
    </S.Wrapper>
  );
};
